import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
// dark-theme overrides shared with CustomDatePicker
import '../custom_date_picker/date_picker.css';
import '../form_layout/form_layout.css';

import { IST, requested_at_confirm } from './care_request_rules';
import {
  is_time_allowed,
  picker_date_to_value,
  picker_max_date,
  value_to_picker_date
} from './ist_picker_rules';
import {
  before_booking_hint,
  booked_picker_date,
  is_day_before_booking,
  is_time_before_booking
} from '../request_time_warning/request_time_warning_rules';
import '../request_time_warning/request_time_warning.css';

export const PICKER_DATE_FORMAT = 'dd/MM/yyyy, hh:mm aa';

// Care "requested at" field: calendar + time list with "Use this time" inside the pop-up (Nithun:
// the native datetime-local pop-up has no confirm). Calendar picks stay pending until Use this
// time; typed text commits on blur/Enter. value/onChange keep the 'YYYY-MM-DDTHH:mm' wall-clock
// string in ianaTimezone, so callers and their submit checks are unchanged.
export function IstTimePicker(props) {
  const { id, value, onChange, ianaTimezone, zoneLabel, className, bookedAt } = props;
  // A-52: days/times before the booking are marked (not disabled); the server warns on save
  const booked = booked_picker_date(bookedAt, ianaTimezone);
  const bookedHint = booked ? before_booking_hint(bookedAt, ianaTimezone) : '';
  const pickerRef = useRef(null);
  const [pending, setPending] = useState(() => value_to_picker_date(value));
  // the time was typed (commit on blur), not picked (commit on the button)
  const typedRef = useRef(false);
  const [maxDate, setMaxDate] = useState(() => picker_max_date(Date.now(), ianaTimezone));
  const [confirmed, setConfirmed] = useState(null);
  // focus handed back after closing must not reopen the calendar (cleared on ArrowDown or when focus moves to another element)
  const [quietFocus, setQuietFocus] = useState(false);

  // follow the host's value (another flow, a reset) unless a typed edit is in progress
  useEffect(() => {
    if (!typedRef.current) setPending(value_to_picker_date(value));
  }, [value]);

  const commit = (next) => {
    typedRef.current = false;
    setConfirmed({
      value: next,
      ...requested_at_confirm(next, Date.now(), ianaTimezone, zoneLabel)
    });
    if (next !== (value || '')) onChange(next);
  };
  // two ticks: the picker queues its own input.blur() (setTimeout 0) on Enter / when it closes,
  // and focusing before it runs would be undone
  const refocus = () => {
    const picker = pickerRef.current;
    setTimeout(() =>
      setTimeout(() => {
        if (picker && picker.input) picker.input.focus();
      })
    );
  };
  // close and hand focus back to the input (keyboard users stay in place) without reopening it
  const close = () => {
    const picker = pickerRef.current;
    if (!picker) return;
    picker.setOpen(false, true); // skip the picker's own blur: focus goes back to the input
    setQuietFocus(true);
    refocus();
  };
  const commitTyped = () => {
    if (typedRef.current) commit(picker_date_to_value(pending));
  };

  const useThisTime = () => {
    commit(picker_date_to_value(pending));
    close();
  };
  const cancel = () => {
    typedRef.current = false;
    setPending(value_to_picker_date(value));
    close();
  };
  const clear = () => {
    setPending(null);
    commit('');
    close();
  };

  // stale once the field changes after the commit
  const shown =
    confirmed && (confirmed.value === '' || confirmed.value === (value || '')) ? confirmed : null;
  const statusId = `${id}-status`;
  const warnId = `${id}-before-booking`;
  const valueBeforeBooking = is_time_before_booking(value_to_picker_date(value), booked);
  const describedBy = [props.ariaDescribedBy, statusId, valueBeforeBooking ? warnId : '']
    .filter(Boolean)
    .join(' ');
  // Escape closes only the open calendar: a parent modal listens for it on document and would
  // drop the whole form. Bubble phase, after the picker has handled it (the open state is still current), covers the portaled pop-up too.
  const guardEscape = (e) => {
    if (e.key === 'Escape' && pickerRef.current && pickerRef.current.isCalendarOpen()) {
      e.nativeEvent.stopPropagation();
    }
  };
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="ist-time-picker" onKeyDown={guardEscape}>
      <DatePicker
        ref={pickerRef}
        id={id}
        selected={pending}
        onChange={(date, event) => {
          // typed text arrives as the input's change event; calendar picks as click/keydown
          typedRef.current = !!event && event.type === 'change';
          setPending(date);
        }}
        onBlur={(e) => {
          // only a real move to another element re-arms open-on-focus: the picker's own
          // programmatic blur while closing has no relatedTarget
          if (e && e.relatedTarget) setQuietFocus(false);
          commitTyped();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && typedRef.current) {
            commitTyped();
            // after the picker's own Enter handling, which re-opens a calendar that is already closed
            setTimeout(close);
          } else if (e.key === 'Enter' && pickerRef.current.isCalendarOpen()) {
            // the picker blurs the input on Enter; keep keyboard focus here so Escape / Tab still work
            refocus();
          } else if (e.key === 'Escape' && pickerRef.current.isCalendarOpen()) {
            // the picker hands focus back to the input on Escape; that must not reopen the calendar
            setQuietFocus(true);
          } else if (quietFocus && e.key === 'ArrowDown') {
            setQuietFocus(false);
            pickerRef.current.setOpen(true);
          }
        }}
        preventOpenOnFocus={quietFocus}
        onCalendarOpen={() => setMaxDate(picker_max_date(Date.now(), ianaTimezone))}
        // closed without Use this time: a picked (not typed) time is dropped, the field shows the saved one
        onCalendarClose={() => {
          if (typedRef.current) commitTyped();
          else setPending(value_to_picker_date(value));
        }}
        showTimeSelect
        timeIntervals={5}
        timeCaption="Time"
        filterTime={(t) => is_time_allowed(t, maxDate)}
        dayClassName={(d) => (is_day_before_booking(d, booked) ? 'rt-before-booking' : '')}
        timeClassName={(t) => (is_time_before_booking(t, booked) ? 'rt-before-booking' : '')}
        maxDate={maxDate}
        dateFormat={PICKER_DATE_FORMAT}
        placeholderText="dd/mm/yyyy, hh:mm AM"
        shouldCloseOnSelect={false}
        autoComplete="off"
        ariaDescribedBy={describedBy}
        ariaInvalid={props.ariaInvalid}
        ariaRequired={props.ariaRequired}
        className={className}
        calendarClassName="ist-time-picker-calendar"
        popperPlacement="bottom-start"
        // portaled so a scrolling pop-up (overflow: auto) cannot clip the calendar
        portalId="datepicker-portal"
      >
        <div className="ist-time-picker-footer">
          <p className="ist-time-picker-zone">Times are in {zoneLabel}</p>
          {bookedHint ? <p className="ist-time-picker-booked">{bookedHint}</p> : null}
          <div className="ist-time-picker-actions">
            {value || pending ? (
              <button type="button" className="myt-btn-secondary" onClick={clear}>
                Clear
              </button>
            ) : null}
            <button type="button" className="myt-btn-secondary" onClick={cancel}>
              Cancel
            </button>
            <button type="button" className="myt-btn-primary" onClick={useThisTime}>
              Use this time
            </button>
          </div>
        </div>
      </DatePicker>
      <span
        id={statusId}
        role="status"
        className={shown && !shown.ok ? 'myt-form-error' : 'myt-form-help'}
      >
        {shown ? shown.text : ''}
      </span>
      {valueBeforeBooking ? (
        <span id={warnId} className="ist-time-picker-warning" data-testid="ist-before-booking">
          {bookedHint}
        </span>
      ) : null}
    </div>
  );
}

IstTimePicker.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  ianaTimezone: PropTypes.string,
  zoneLabel: PropTypes.string,
  className: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
  ariaInvalid: PropTypes.string,
  ariaRequired: PropTypes.string,
  // when the class was booked (ISO / DB string / epoch ms); earlier days and times get a warning style
  bookedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

IstTimePicker.defaultProps = {
  value: '',
  ianaTimezone: IST,
  zoneLabel: 'IST',
  className: '',
  ariaDescribedBy: undefined,
  ariaInvalid: undefined,
  ariaRequired: undefined,
  bookedAt: undefined
};

export default IstTimePicker;
